"use client"

import { useEffect, useState } from "react"
import { discardStoredRecording, resumeUpload } from "@/services/recording-resilient-uploader"
import { ProcessingStatus, useMeetingRecordingStore } from "@/stores/use-meeting-recording-store"
import { useQueryClient } from "@tanstack/react-query"
import { Button } from "@ui/shad"
import { Check, Loader2, RefreshCw, Trash2, WifiOff, X } from "lucide-react"
import { toast } from "sonner"

import { isMeetingPromptFinished, isMeetingPromptInProgress } from "@/lib/prompt-status"
import { useMeeting } from "@/hooks/api/meetings"
import { useApiContext } from "@/hooks/api/use-api-context"
import { Link } from "@/components/book-link"
import { ConfirmDialog } from "@/components/dialogs"

const PROCESSING_STEPS: { status: ProcessingStatus; label: string }[] = [
  { status: "uploading", label: "Uploading audio..." },
  { status: "concatenating", label: "Processing audio..." },
  { status: "transcribing", label: "Transcribing..." },
  { status: "generating_notes", label: "Generating notes..." },
  { status: "completed", label: "All done!" },
]

function getStepIndex(status: ProcessingStatus): number {
  return PROCESSING_STEPS.findIndex((s) => s.status === status)
}

export function ProcessingStatusView() {
  const {
    isRecording,
    isRecordingTransferActive,
    processingMeetingId,
    processingMeetingTitle,
    processingStatus,
    processingSessionId,
    processingSessionGuid,
    processingMimeType,
    processingRequiresConfirmation,
    updateProcessingStatus,
    clearProcessing,
    startProcessing,
  } = useMeetingRecordingStore()

  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const [isRetrying, setIsRetrying] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [isDiscarding, setIsDiscarding] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true)

  const { data: meeting, isFetching } = useMeeting(processingMeetingId || "", {
    enabled:
      !!processingMeetingId &&
      !isRecordingTransferActive &&
      processingStatus !== "completed" &&
      processingStatus !== "upload_pending",
    refetchInterval:
      processingStatus !== "completed" && processingStatus !== "failed" && processingStatus !== "upload_pending"
        ? 3000
        : false,
  })

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  useEffect(() => {
    if (
      processingStatus === "upload_pending" &&
      isOnline &&
      !isRetrying &&
      !isDiscarding &&
      !isRecording &&
      !processingRequiresConfirmation
    ) {
      handleRetryUpload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, processingStatus, isRecording, processingRequiresConfirmation])

  useEffect(() => {
    if (
      !meeting ||
      isRecording ||
      isRecordingTransferActive ||
      isFetching ||
      processingStatus === "completed" ||
      processingStatus === "upload_pending"
    )
      return

    if (meeting.live_recording_session) {
      updateProcessingStatus(meeting.live_recording_session.status === "concatenating" ? "concatenating" : "uploading")
      return
    }

    if (meeting.recording_capture_warning && !meeting.is_processing_recording && !meeting.live_recording_session) {
      updateProcessingStatus("failed")
      return
    }

    const notesPromptFinished = isMeetingPromptFinished(meeting, "Prompt::MeetingNote")
    const notesPromptInProgress = isMeetingPromptInProgress(meeting, "Prompt::MeetingNote")
    const transcriptionDone = meeting.has_recordings && !meeting.is_processing_recording

    if (transcriptionDone && notesPromptFinished) {
      updateProcessingStatus("completed")
    } else if (notesPromptInProgress || transcriptionDone) {
      updateProcessingStatus("generating_notes")
    } else if (meeting.is_processing_recording) {
      updateProcessingStatus("transcribing")
    } else if (meeting.has_recordings && processingStatus === "uploading") {
      updateProcessingStatus("concatenating")
    }
  }, [meeting, isRecording, isRecordingTransferActive, isFetching, processingStatus, updateProcessingStatus])

  const handleRetryUpload = async (recoverInterrupted = false) => {
    if (isDiscarding || isRetrying) return
    if (!processingMeetingId || !processingSessionId || !processingSessionGuid || !processingMimeType) return

    setIsRetrying(true)
    setRetryError(null)
    try {
      const result = await resumeUpload(
        processingMeetingId,
        processingSessionId,
        processingSessionGuid,
        getContext,
        processingMimeType,
        undefined,
        recoverInterrupted
      )
      await queryClient.invalidateQueries({ queryKey: ["meeting"] })
      await queryClient.invalidateQueries({ queryKey: ["meetings"] })
      if (result.outcome === "abandoned") {
        clearProcessing()
        toast.error("No saved audio was available to recover.")
      } else {
        startProcessing(processingMeetingId, processingMeetingTitle)
      }
    } catch (error) {
      setRetryError(
        error instanceof Error ? error.message : "Upload failed. Your saved audio is still available to retry."
      )
    } finally {
      setIsRetrying(false)
    }
  }

  const handleDiscard = async () => {
    if (!processingMeetingId || !processingSessionId || !processingSessionGuid) return
    if (isRetrying || isDiscarding) return
    setIsDiscarding(true)
    try {
      await discardStoredRecording(processingMeetingId, processingSessionId, processingSessionGuid, getContext)
      clearProcessing()
      toast.success("Recording discarded")
    } catch (error) {
      setRetryError(error instanceof Error ? error.message : "Could not discard this recording")
    } finally {
      setIsDiscarding(false)
      setShowDiscardDialog(false)
    }
  }

  if (!processingMeetingId) return null

  const title = processingMeetingTitle || meeting?.title || "Untitled meeting"

  if (processingStatus === "upload_pending") {
    return (
      <div className="flex w-full flex-col p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-background truncate text-base font-medium">{title}</span>
          <Button
            size="iconXs"
            variant="ghost"
            onClick={() => setShowDiscardDialog(true)}
            disabled={isRetrying || isDiscarding}
            title="Discard recording"
            className="text-icon-muted hover:text-background hover:bg-white/10"
          >
            <Trash2 />
          </Button>
        </div>

        <div className="space-y-3">
          {retryError && (
            <p role="alert" className="text-yellow-accent text-sm">
              {retryError}
            </p>
          )}
          {processingRequiresConfirmation && (
            <p role="alert" className="text-yellow-accent text-sm">
              Recording did not finish. Recover the saved audio after closing the original recording tab.
            </p>
          )}
          {!isOnline ? (
            <div className="flex items-center gap-2">
              <WifiOff className="text-yellow-accent size-4" />
              <span className="text-background/80 text-sm">Will upload when back online</span>
            </div>
          ) : isRetrying ? (
            <div className="flex items-center gap-2">
              <Loader2 className="text-background size-4 animate-spin" />
              <span className="text-background text-sm">Finishing upload...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="bg-yellow-accent size-4 rounded-full" />
              <span className="text-background text-sm">Upload paused</span>
            </div>
          )}

          {isOnline && !isRetrying && (
            <Button
              size="sm"
              variant="inverse"
              onClick={() => handleRetryUpload(true)}
              disabled={isDiscarding}
              className="w-full"
            >
              <RefreshCw />
              {processingRequiresConfirmation ? "Recover saved audio" : "Retry upload"}
            </Button>
          )}
        </div>
        <ConfirmDialog
          open={showDiscardDialog}
          onOpenChange={(open) => !isDiscarding && setShowDiscardDialog(open)}
          title="Discard recording?"
          description="This will delete the saved audio. This cannot be undone."
          cancel={{ disabled: isDiscarding }}
          action={{
            label: "Discard recording",
            onClick: handleDiscard,
            destructive: true,
            pending: isDiscarding,
            disabled: isRetrying,
          }}
        />
      </div>
    )
  }

  const currentStepIndex = getStepIndex(processingStatus)
  const isComplete = processingStatus === "completed"

  return (
    <div className="flex w-full flex-col p-4">
      <div className="mb-3 flex items-center justify-between">
        {isComplete ? (
          <Link href={`/meetings/${processingMeetingId}`} onClick={clearProcessing}>
            <span className="text-background truncate text-base underline decoration-dotted underline-offset-2">
              {title}
            </span>
          </Link>
        ) : (
          <span className="text-background truncate text-base font-medium">{title}</span>
        )}
        <Button
          size="iconXs"
          variant="ghost"
          onClick={clearProcessing}
          className="text-icon-muted hover:text-background hover:bg-white/10"
        >
          <X />
        </Button>
      </div>

      <div className="space-y-2">
        {meeting?.recording_capture_warning && (
          <p role="alert" className="text-yellow-accent text-sm">
            {meeting.recording_capture_warning}
          </p>
        )}
        {PROCESSING_STEPS.slice(0, -1).map((step, index) => {
          const isActive = index === currentStepIndex
          const isCompleted = index < currentStepIndex || processingStatus === "completed"

          return (
            <div key={step.status} className="flex items-center gap-2">
              {isCompleted ? (
                <div className="bg-green-accent flex size-4 items-center justify-center rounded-full">
                  <Check className="size-2.5 text-white" />
                </div>
              ) : isActive ? (
                <Loader2 className="text-background size-4 animate-spin" />
              ) : (
                <div className="bg-background/30 size-4 rounded-full" />
              )}
              <span
                className={
                  isActive
                    ? "text-background text-sm font-medium"
                    : isCompleted
                      ? "text-background/80 text-sm"
                      : "text-background/50 text-sm"
                }
              >
                {step.label}
              </span>
            </div>
          )
        })}
      </div>

      {(isComplete || processingStatus === "failed") && (
        <div className="mt-4">
          <Link href={`/meetings/${processingMeetingId}`} onClick={clearProcessing}>
            <Button size="sm" variant="inverse" className="w-full">
              {processingStatus === "failed" ? "Review recording" : "View meeting notes"}
            </Button>
          </Link>
        </div>
      )}
    </div>
  )
}
